/**
 * Kiosk template builder (Phase 4 T06). Route: /kiosks/templates/:templateId
 *
 * Non-developer canvas editor per
 * `plans/designs/phase-4-kiosk-ui-design.md` §Template-builder canvas
 * interaction + §Version lifecycle:
 * - Palette comes ONLY from `GET /v1/kiosks/widgets` descriptors (labels,
 *   min sizes, field descriptors) — no embedded catalog copy.
 * - Placement/resize/zIndex edits are validated against the frozen geometry
 *   imported from `@cluster/kiosk-renderer` (FROZEN_RENDER_GEOMETRY) and the
 *   catalog minSize — never re-hardcoded; out-of-bounds edits are refused.
 * - Live preview runs the SAME shared pipeline as the kiosk device
 *   (`prepareRenderPlan` over the frozen schemaVersion:1 envelope): on
 *   `ok:false` every typed issue is shown and NOTHING is rendered partial.
 * - Discount preview honors the frozen 7-element/7-state contract with
 *   boolean-only presentation selection and clearly-labeled fixture strings;
 *   the builder performs zero money computation (all values are opaque
 *   server-returned display strings).
 * - Published versions are immutable in UI (no edit affordances) because the
 *   API rejects edits to a frozen version; changes start via duplicate or a
 *   new draft save after publish.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FROZEN_RENDER_GEOMETRY as GEOMETRY,
  DISCOUNT_FIELD_KEYS,
  DISCOUNT_STATES,
  prepareRenderPlan,
  computeViewportScale,
  scaleRect,
  selectDisplayableDiscount,
  type DiscountState,
  type PlacedWidget,
  type RendererIssue,
} from '@cluster/kiosk-renderer';
import type {
  KioskLayoutWidget,
  KioskTemplateVisibility,
  KioskWidgetDescriptor,
} from '@cluster/types';

import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CANVAS,
  DISCOUNT_FIXTURE_FIELDS,
  buildDraftRenderDocument,
  formatKioskDate,
  kioskApiErrorText,
  KioskLoadError,
  KioskNavigation,
  nextWidgetId,
  parseIdList,
  validateLayoutForSave,
  validateVisibilityRule,
  validateWidgetPlacement,
  type PlacementIssue,
} from './KioskShared';
import {
  useDuplicateKioskTemplateMutation,
  useLazyGetKioskTemplatePreviewQuery,
  useGetKioskTemplateQuery,
  useGetKioskWidgetsQuery,
  usePublishKioskTemplateMutation,
  useSaveKioskTemplateVersionMutation,
} from '@/store/api/kiosksApi';

const PREVIEW_FIXTURE_NOTE = 'Preview fixture — not live data. Discount strings come from the server at runtime; the builder never computes them.';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

interface DraftState {
  layout: KioskLayoutWidget[];
  visibility: KioskTemplateVisibility | null;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function KioskTemplateBuilderPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const templateQuery = useGetKioskTemplateQuery(templateId ?? '', { skip: !templateId });
  const widgetsQuery = useGetKioskWidgetsQuery();
  const [duplicateTemplate, duplicateState] = useDuplicateKioskTemplateMutation();

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [placementIssues, setPlacementIssues] = useState<PlacementIssue[]>([]);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [actionError, setActionError] = useState('');
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [viewport, setViewport] = useState<'720p' | '1080p'>('720p');
  const [discountState, setDiscountState] = useState<DiscountState>('applied');
  const [serverPreviewIssues, setServerPreviewIssues] = useState<readonly RendererIssue[] | null>(null);
  const [showPublishReview, setShowPublishReview] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [loadSavedPreview, savedPreviewState] = useLazyGetKioskTemplatePreviewQuery();

  const template = templateQuery.data;
  const currentVersionRecord = useMemo(
    () => template?.versions.find((version) => version.version === template.currentVersion),
    [template],
  );
  const isFrozen = Boolean(currentVersionRecord?.publishedAt);
  const isArchived = template?.status === 'archived';
  // A frozen version can never be edited in place: all canvas affordances are
  // disabled and changes route through the service's duplicate/new-draft
  // semantics only (design doc: "Published version becomes immutable").
  const readOnly = isArchived || isFrozen;

  // Seed the local draft only while nothing is being edited, so a background
  // refetch can never silently discard operator changes.
  useEffect(() => {
    if (!template || !currentVersionRecord || saveState === 'dirty' || saveState === 'saving') return;
    setDraft({
      layout: currentVersionRecord.layout.map((widget) => ({
        ...widget,
        position: { ...widget.position },
      })),
      visibility: {
        channel: 'kiosk',
        categories: { ...currentVersionRecord.visibility.categories },
        products: { ...currentVersionRecord.visibility.products },
        featuredProducts: { ...currentVersionRecord.visibility.featuredProducts },
        discounts: { ...currentVersionRecord.visibility.discounts },
      },
    });
    // Re-seed on template/version change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id, template?.currentVersion, currentVersionRecord?.layout?.length]);

  const descriptors = useMemo(() => {
    const map = new Map<string, KioskWidgetDescriptor>();
    for (const descriptor of widgetsQuery.data ?? []) map.set(descriptor.type, descriptor);
    return map;
  }, [widgetsQuery.data]);

  const selectedWidget = draft?.layout.find((widget) => widget.widgetId === selectedWidgetId) ?? null;
  const selectedDescriptor = selectedWidget ? descriptors.get(selectedWidget.type) ?? null : null;

  const draftIssues = useMemo(() => {
    if (!draft || !draft.visibility) return ['Kiosk draft is still loading.'];
    if (!widgetsQuery.data) return ['Widget catalog is still loading.'];
    return [
      ...validateLayoutForSave(draft.layout, descriptors),
      ...(['categories', 'products', 'featuredProducts', 'discounts'] as const)
        .flatMap((collection) => validateVisibilityRule(draft.visibility![collection])),
    ];
  }, [draft, descriptors, widgetsQuery.data]);

  const planOutcome = useMemo(() => {
    if (!templateId || !draft || !draft.visibility) return null;
    return prepareRenderPlan(
      buildDraftRenderDocument(templateId, currentVersionRecord?.version ?? template?.currentVersion ?? 1, draft.layout, draft.visibility),
    );
  }, [templateId, draft, currentVersionRecord?.version, template?.currentVersion]);

  const markDirty = useCallback(() => {
    setSaveState('dirty');
    setActionError('');
  }, []);

  function addWidget(descriptor: KioskWidgetDescriptor, at?: { x: number; y: number }) {
    if (!draft || readOnly) return;
    if (draft.layout.length >= GEOMETRY.maxWidgets) {
      setActionError(`A layout holds at most ${GEOMETRY.maxWidgets} widgets.`);
      return;
    }
    const w = descriptor.minSize.w;
    const h = descriptor.minSize.h;
    const widget: KioskLayoutWidget = {
      widgetId: nextWidgetId(draft.layout, descriptor.type),
      type: descriptor.type,
      position: {
        x: clamp(Math.round(at?.x ?? 40 + draft.layout.length * 24), 0, CANVAS.width - w),
        y: clamp(Math.round(at?.y ?? 40 + draft.layout.length * 24), 0, CANVAS.height - h),
        w,
        h,
      },
      config: { ...descriptor.defaultProps },
      zIndex: 0,
    };
    setDraft({ ...draft, layout: [...draft.layout, widget] });
    setSelectedWidgetId(widget.widgetId);
    setPlacementIssues([]);
    markDirty();
  }

  function updateWidget(next: KioskLayoutWidget) {
    if (!draft || readOnly) return;
    const descriptor = descriptors.get(next.type);
    const issues = validateWidgetPlacement(next, descriptor?.minSize ?? { w: 1, h: 1 }, draft.layout);
    setPlacementIssues(issues);
    if (issues.length > 0) return; // refuse the edit; last valid geometry stays
    setDraft({ ...draft, layout: draft.layout.map((widget) => (widget.widgetId === next.widgetId ? next : widget)) });
    markDirty();
  }

  function updateWidgetConfig(widgetId: string, name: string, value: unknown) {
    if (!draft || readOnly) return;
    setDraft({
      ...draft,
      layout: draft.layout.map((widget) =>
        widget.widgetId === widgetId ? { ...widget, config: { ...widget.config, [name]: value } } : widget,
      ),
    });
    markDirty();
  }

  function removeWidget(widgetId: string) {
    if (!draft || readOnly) return;
    setDraft({ ...draft, layout: draft.layout.filter((widget) => widget.widgetId !== widgetId) });
    if (selectedWidgetId === widgetId) setSelectedWidgetId(null);
    markDirty();
  }

  function updateVisibility(collection: 'categories' | 'products' | 'featuredProducts' | 'discounts', key: 'allowIds' | 'denyIds', raw: string) {
    if (!draft?.visibility || readOnly) return;
    const ids = parseIdList(raw);
    const rule = { ...draft.visibility[collection], [key]: ids };
    const issues = validateVisibilityRule(rule);
    setPlacementIssues(issues.map((message) => ({ field: 'widgetId' as const, message })));
    if (issues.length > 0) return;
    setDraft({ ...draft, visibility: { ...draft.visibility, [collection]: rule } });
    markDirty();
  }

  const [saveDraft] = useSaveKioskTemplateVersionMutation();
  const [publishTemplate] = usePublishKioskTemplateMutation();

  /** Returns true only when the draft was accepted by the service. */
  async function persistDraft(): Promise<boolean> {
    if (!templateId || !draft?.visibility || draftIssues.length > 0) return false;
    setSaveState('saving');
    setActionError('');
    try {
      await saveDraft({ id: templateId, body: { layout: draft.layout, visibility: draft.visibility } }).unwrap();
      setSaveState('saved');
      await templateQuery.refetch();
      return true;
    } catch (caught) {
      setSaveState('failed');
      setActionError(kioskApiErrorText(caught));
      return false;
    }
  }

  async function publish() {
    if (!templateId || draftIssues.length > 0) return;
    setActionError('');
    try {
      // The service re-validates server-side; if the draft is unsaved the
      // publish freezes whatever is stored — save first, then publish. A
      // refused save must ABORT the publish: freezing a stale stored version
      // the operator never saved would silently discard their edits.
      if (draft?.visibility && saveState === 'dirty') {
        const saved = await persistDraft();
        if (!saved) return;
      }
      if (draftIssues.length > 0) return;
      await publishTemplate(templateId).unwrap();
      setShowPublishReview(false);
      setSaveState('idle');
      await templateQuery.refetch();
    } catch (caught) {
      setActionError(kioskApiErrorText(caught));
    }
  }

  async function duplicateToDraft() {
    if (!templateId) return;
    setActionError('');
    try {
      const result = await duplicateTemplate({ id: templateId, body: { version: currentVersionRecord?.version } }).unwrap();
      navigate(`/kiosks/templates/${encodeURIComponent(result.template.id)}`);
    } catch (caught) {
      setActionError(kioskApiErrorText(caught));
    }
  }

  async function loadServerPreview() {
    if (!templateId || !currentVersionRecord) return;
    setActionError('');
    setServerPreviewIssues(null);
    try {
      const document = await loadSavedPreview({ id: templateId, version: currentVersionRecord.version }).unwrap();
      // The device path and the builder both run the SAME pipeline:
      const outcome = prepareRenderPlan(document);
      if (!outcome.ok) setServerPreviewIssues(outcome.issues);
    } catch (caught) {
      setActionError(kioskApiErrorText(caught));
    }
  }

  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ widgetId: string; startClientX: number; startClientY: number; originX: number; originY: number } | null>(null);

  function canvasScaleFactor() {
    const rect = canvasRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 ? CANVAS.width / rect.width : 1;
  }

  function beginWidgetDrag(event: React.PointerEvent<HTMLDivElement>, widget: KioskLayoutWidget) {
    if (readOnly || mode !== 'edit') return;
    setSelectedWidgetId(widget.widgetId);
    dragRef.current = {
      widgetId: widget.widgetId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: widget.position.x,
      originY: widget.position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveWidgetDrag(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || !draft) return;
    const widget = draft.layout.find((candidate) => candidate.widgetId === drag.widgetId);
    if (!widget) return;
    const factor = canvasScaleFactor();
    const next: KioskLayoutWidget = {
      ...widget,
      position: {
        ...widget.position,
        // Drag keeps the widget inside the canvas: the delta is clamped so an
        // out-of-bounds placement is impossible (design: "Prevent off-canvas
        // placement"), and everything stays integer pixel geometry.
        x: clamp(Math.round(drag.originX + (event.clientX - drag.startClientX) * factor), 0, CANVAS.width - widget.position.w),
        y: clamp(Math.round(drag.originY + (event.clientY - drag.startClientY) * factor), 0, CANVAS.height - widget.position.h),
      },
    };
    if (next.position.x === widget.position.x && next.position.y === widget.position.y) return;
    setDraft({ ...draft, layout: draft.layout.map((candidate) => (candidate.widgetId === drag.widgetId ? next : candidate)) });
    markDirty();
  }

  function endWidgetDrag() {
    dragRef.current = null;
  }

  function handleCanvasDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/x-kiosk-widget');
    const descriptor = descriptors.get(type);
    if (!descriptor || !draft || readOnly) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    const factor = rect && rect.width > 0 ? CANVAS.width / rect.width : 1;
    const x = rect ? (event.clientX - rect.left) * factor : 40;
    const y = rect ? (event.clientY - rect.top) * factor : 40;
    addWidget(descriptor, { x, y });
  }

  if (!templateId) return <KioskLoadError label="template (missing ID in route)" retry={() => navigate('/kiosks/templates')} />;
  if (templateQuery.isLoading) return <LoadingRows label="Loading kiosk template" columns={3} />;
  if (templateQuery.isError) return <KioskLoadError label="kiosk template" retry={() => void templateQuery.refetch()} />;
  if (!template || !draft || !draft.visibility) return <LoadingRows label="Preparing kiosk draft" columns={3} />;

  const versionNumber = currentVersionRecord?.version ?? template.currentVersion;
  const statusCopy = isArchived
    ? 'Archived — no further edits'
    : isFrozen
      ? `Published v${versionNumber} — frozen`
      : `Draft v${versionNumber} — editable`;
  const saveCopy = saveState === 'dirty'
    ? 'Unsaved changes'
    : saveState === 'saving'
      ? 'Saving…'
      : saveState === 'saved'
        ? 'Saved'
        : saveState === 'failed'
          ? 'Validation failed'
          : null;
  const previewScale = computeViewportScale(viewport === '1080p' ? { width: 640, height: 360 } : { width: 426, height: 240 });

  return (
    <section className="space-y-4">
      <KioskNavigation active="templates" />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{template.name}</h1>
          <p className="text-sm text-muted-foreground">
            Template ID <span className="font-mono">{template.id}</span> · v{versionNumber}
            {template.storeId && <> · Store <span className="font-mono">{template.storeId}</span></>}
            {template.workspaceId && <> · Workspace <span className="font-mono">{template.workspaceId}</span></>}
          </p>
          <p className="mt-1 text-sm font-medium" aria-live="polite">
            {statusCopy}
            {saveCopy && <span className="ml-3 text-muted-foreground">{saveCopy}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={mode === 'edit' ? 'secondary' : 'ghost'} onClick={() => setMode('edit')}>Edit</Button>
          <Button variant={mode === 'preview' ? 'secondary' : 'ghost'} onClick={() => setMode('preview')}>Preview</Button>
          {!isFrozen && (
            <Button
              variant="outline"
              disabled={readOnly || saveState === 'saving' || draftIssues.length > 0}
              title={draftIssues.length > 0 ? 'Fix the validation issues before saving' : undefined}
              onClick={() => void persistDraft()}
            >
              {saveState === 'saving' ? 'Saving…' : 'Save draft'}
            </Button>
          )}
          {!isFrozen && !isArchived && (
            <Button disabled={draftIssues.length > 0 || saveState === 'saving'} onClick={() => setShowPublishReview(true)}>
              Publish version
            </Button>
          )}
          {(isFrozen || isArchived) && (
            <Button variant="outline" disabled={duplicateState.isLoading} onClick={() => void duplicateToDraft()}>
              {duplicateState.isLoading ? 'Duplicating…' : 'Duplicate to draft'}
            </Button>
          )}
        </div>
      </header>

      {isFrozen && (
        <div role="note" className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          This version is published and can&apos;t be edited. Duplicate it to make changes.
          <span className="mt-1 block text-muted-foreground">
            Editing, saving, and re-publishing this frozen version are disabled here — the service rejects
            mutations to published versions with an explicit error.
          </span>
        </div>
      )}
      {isArchived && (
        <div role="note" className="rounded-md border border-destructive/40 p-3 text-sm">
          This template is archived. Archived templates can&apos;t be edited or published; duplicate is the only way to reuse its content.
        </div>
      )}
      {actionError && <p role="alert" className="text-sm text-destructive">{actionError}</p>}

      {/*
        Blocking issues must be visible OUTSIDE the publish dialog. Both
        "Save draft" and "Publish version" are disabled while any exist, and
        the dialog is unreachable from a disabled button — so without this
        panel the buttons looked dead with no stated reason.
      */}
      {draftIssues.length > 0 && !readOnly && (
        <div role="status" aria-live="polite" className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <p className="font-medium">
            Saving and publishing are blocked by {draftIssues.length} issue{draftIssues.length === 1 ? '' : 's'}:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs">
            {draftIssues.map((issue) => <li key={issue}>{issue}</li>)}
          </ul>
        </div>
      )}

      {mode === 'edit' && (
        <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)_300px]">
          {/* ── Palette: descriptors ONLY from GET /v1/kiosks/widgets ── */}
          <aside className="space-y-2" aria-label="Widget catalog palette">
            <h2 className="text-sm font-semibold">Widgets</h2>
            {widgetsQuery.isLoading && <LoadingRows label="Loading widget catalog" columns={1} rows={3} />}
            {widgetsQuery.isError && (
              <KioskLoadError label="widget catalog" retry={() => void widgetsQuery.refetch()} />
            )}
            {widgetsQuery.data && widgetsQuery.data.length === 0 && (
              <EmptyPaletteNote onRetry={() => void widgetsQuery.refetch()} />
            )}
            {widgetsQuery.data?.map((descriptor) => (
              <div
                key={descriptor.type}
                draggable={!readOnly}
                onDragStart={(event) => event.dataTransfer.setData('application/x-kiosk-widget', descriptor.type)}
                className="rounded-md border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{descriptor.label}</span>
                  <Button
                    type="button"
                    size="sm"
                    className="min-h-[44px] min-w-[44px]"
                    disabled={readOnly}
                    onClick={() => addWidget(descriptor)}
                    aria-label={`Add ${descriptor.label} widget`}
                  >
                    Add
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{descriptor.description}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Min {descriptor.minSize.w}×{descriptor.minSize.h} · drag onto the canvas or press Add
                </p>
              </div>
            ))}
          </aside>

          {/* ── Canvas: 16:9 authoring surface, bounded to the region ── */}
          <div
            ref={canvasRef}
            role="group"
            aria-label={`Kiosk canvas, ${CANVAS.width} by ${CANVAS.height} pixels`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleCanvasDrop}
            onPointerMove={moveWidgetDrag}
            onPointerUp={endWidgetDrag}
            className="relative aspect-video w-full touch-none overflow-hidden rounded-md border bg-muted/30"
            style={{ backgroundImage: 'linear-gradient(to right, rgba(120,120,120,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,120,120,0.12) 1px, transparent 1px)', backgroundSize: `${100 / 24}% ${100 / 13.5}%` }}
          >
            {draft.layout.length === 0 && (
              <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                The canvas is empty. Add a widget from the palette.
              </p>
            )}
            {[...draft.layout]
              .sort((left, right) => left.zIndex - right.zIndex)
              .map((widget) => {
                const descriptor = descriptors.get(widget.type);
                const selected = widget.widgetId === selectedWidgetId;
                return (
                  <div
                    key={widget.widgetId}
                    role="button"
                    tabIndex={0}
                    aria-label={`${descriptor?.label ?? widget.type}, ${widget.position.x} ${widget.position.y}, ${widget.position.w} by ${widget.position.h}`}
                    aria-pressed={selected}
                    onPointerDown={(event) => beginWidgetDrag(event, widget)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedWidgetId(widget.widgetId);
                      }
                    }}
                    className={`absolute flex items-center justify-center overflow-hidden border p-1 text-center text-xs ${selected ? 'border-primary bg-primary/10' : 'border-muted-foreground/40 bg-background/70'} ${readOnly ? '' : 'cursor-grab'}`}
                    style={{
                      left: `${(widget.position.x / CANVAS.width) * 100}%`,
                      top: `${(widget.position.y / CANVAS.height) * 100}%`,
                      width: `${(widget.position.w / CANVAS.width) * 100}%`,
                      height: `${(widget.position.h / CANVAS.height) * 100}%`,
                      zIndex: widget.zIndex + 1,
                    }}
                  >
                    <span className="pointer-events-none truncate font-medium">{descriptor?.label ?? widget.type}</span>
                  </div>
                );
              })}
          </div>

          {/* ── Inspector: placement, catalog-driven config, visibility ── */}
          <aside className="space-y-4" aria-label="Widget and template inspector">
            <section className="space-y-2 rounded-md border p-3" aria-label="Placement inspector">
              <h2 className="text-sm font-semibold">Placement</h2>
              {!selectedWidget && <p className="text-xs text-muted-foreground">Select a widget on the canvas to edit its position, size, layer and content.</p>}
              {selectedWidget && (
                <PlacementEditor
                  widget={selectedWidget}
                  minSize={selectedDescriptor?.minSize ?? { w: 1, h: 1 }}
                  issues={placementIssues}
                  disabled={readOnly}
                  onChange={updateWidget}
                  onRemove={() => removeWidget(selectedWidget.widgetId)}
                />
              )}
            </section>

            {selectedWidget && selectedDescriptor && (
              <section className="space-y-2 rounded-md border p-3" aria-label="Widget content inspector">
                <h2 className="text-sm font-semibold">{selectedDescriptor.label} content</h2>
                <ConfigFieldsEditor
                  descriptor={selectedDescriptor}
                  config={selectedWidget.config}
                  disabled={readOnly}
                  onChange={(name, value) => updateWidgetConfig(selectedWidget.widgetId, name, value)}
                />
              </section>
            )}

            <section className="space-y-3 rounded-md border p-3" aria-label="Visibility inspector">
              <h2 className="text-sm font-semibold">Visibility</h2>
              <p className="text-xs text-muted-foreground">
                Channel is fixed to <span className="font-mono">kiosk</span>. Empty allow list means all eligible
                records; deny IDs always win. Record eligibility itself is decided by the server — the builder never
                infers it.
              </p>
              {(['categories', 'products', 'featuredProducts', 'discounts'] as const).map((collection) => (
                <VisibilityRuleEditor
                  key={collection}
                  collection={collection}
                  rule={draft.visibility![collection]}
                  disabled={readOnly}
                  onChange={updateVisibility.bind(null, collection)}
                />
              ))}
            </section>
          </aside>
        </div>
      )}

      {mode === 'preview' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">
              Preview — not published
              <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs font-medium">v{versionNumber} draft</span>
            </h2>
            <div className="flex items-center gap-2">
              <Label htmlFor="kiosk-preview-viewport" className="text-xs">Viewport</Label>
              <select
                id="kiosk-preview-viewport"
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={viewport}
                onChange={(event) => setViewport(event.target.value as '720p' | '1080p')}
              >
                <option value="720p">1280×720</option>
                <option value="1080p">1920×1080</option>
              </select>
              <Button type="button" variant="outline" disabled={savedPreviewState.isLoading} onClick={() => void loadServerPreview()}>
                Load saved preview
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Read-only preview through the shared <span className="font-mono">@cluster/kiosk-renderer</span> pipeline — the
            exact device render path. It never performs order, payment, or hold actions.
          </p>

          {planOutcome && !planOutcome.ok && (
            <div role="alert" className="space-y-2 rounded-md border border-destructive/40 p-4">
              <p className="text-sm font-medium">Preview unavailable — the draft fails the frozen render contract. Nothing is rendered until every issue is fixed.</p>
              <ul className="space-y-1 text-xs">
                {planOutcome.issues.map((issue, index) => (
                  <li key={index} className="font-mono">{issue.path} · {issue.code} — {issue.message}</li>
                ))}
              </ul>
            </div>
          )}

          {planOutcome?.ok && (
            <div
              className="relative overflow-hidden rounded-md border bg-background"
              style={{ width: Math.round(CANVAS.width * previewScale), height: Math.round(CANVAS.height * previewScale) }}
              aria-label="Draft preview"
            >
              {planOutcome.plan.widgets.length === 0 ? (
                <p className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                  The draft has no widgets yet. Preview is still read-only.
                </p>
              ) : (
                planOutcome.plan.widgets.map((placed) => (
                  <PreviewWidgetBox key={placed.widget.widgetId} placed={placed} scale={previewScale} discountState={discountState} />
                ))
              )}
            </div>
          )}

          {serverPreviewIssues && (
            <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
              <p className="font-medium">The saved render document failed the shared contract:</p>
              <ul className="mt-2 space-y-1 text-xs">
                {serverPreviewIssues.map((issue, index) => (
                  <li key={index} className="font-mono">{issue.path} · {issue.code} — {issue.message}</li>
                ))}
              </ul>
            </div>
          )}

          <section className="space-y-2 rounded-md border p-4" aria-label="Discount presentation preview">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Discount state preview</h3>
              <Label htmlFor="kiosk-discount-state" className="sr-only">Discount state</Label>
              <select
                id="kiosk-discount-state"
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={discountState}
                onChange={(event) => setDiscountState(event.target.value as DiscountState)}
              >
                {DISCOUNT_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">{PREVIEW_FIXTURE_NOTE}</p>
            <DiscountPreview state={discountState} />
          </section>
        </div>
      )}

      {showPublishReview && (
        <div role="dialog" aria-label="Publish review" className="space-y-3 rounded-md border p-4">
          <h2 className="text-lg font-semibold">Publish this version?</h2>
          <p>Publishing freezes this version. Future edits must be made in a new draft.</p>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            <li>Template: <strong>{template.name}</strong> (<span className="font-mono">{template.id}</span>)</li>
            <li>Version to freeze: v{versionNumber}</li>
            <li>Widgets: {draft.layout.length} · layout issues: {draftIssues.length === 0 ? 'none' : `${draftIssues.length} blocking`}</li>
            <li>Scope: org <span className="font-mono">{template.orgId}</span>{template.storeId ? <> · store <span className="font-mono">{template.storeId}</span></> : null}{template.workspaceId ? <> · workspace <span className="font-mono">{template.workspaceId}</span></> : null}</li>
          </ul>
          {draftIssues.length > 0 && (
            <ul role="alert" className="space-y-1 text-sm text-destructive">
              {draftIssues.map((issue) => <li key={issue}>Blocking: {issue}</li>)}
            </ul>
          )}
          <div className="flex gap-2">
            <Button disabled={draftIssues.length > 0} onClick={() => void publish()}>Publish version</Button>
            <Button variant="outline" onClick={() => setShowPublishReview(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {saveState === 'dirty' && confirmLeave && (
        <div role="dialog" aria-label="Unsaved changes" className="space-y-3 rounded-md border border-amber-500/40 p-4 text-sm">
          <p>You have unsaved changes. Leaving now discards them — the saved draft stays exactly as last saved.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmLeave(false)}>Stay on this page</Button>
            <Button variant="destructive" onClick={() => navigate('/kiosks/templates')}>Discard and leave</Button>
          </div>
        </div>
      )}

      {saveState !== 'dirty' && (
        <Button variant="ghost" onClick={() => navigate('/kiosks/templates')}>← Kiosk templates</Button>
      )}
      {saveState === 'dirty' && !confirmLeave && (
        <Button variant="ghost" onClick={() => setConfirmLeave(true)}>← Kiosk templates</Button>
      )}

      <p className="text-xs text-muted-foreground">
        Last updated {formatKioskDate(template.updatedAt)} · current version v{template.currentVersion}
        {currentVersionRecord?.publishedAt && <> · published {formatKioskDate(currentVersionRecord.publishedAt)} by <span className="font-mono">{currentVersionRecord.publishedBy}</span></>}
      </p>
    </section>
  );
}

function EmptyPaletteNote({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm">
      <p>No widgets are available.</p>
      <Button type="button" variant="outline" onClick={onRetry}>Try again</Button>
    </div>
  );
}

function NumberField({ id, label, value, min, max, disabled, onCommit }: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onCommit: (next: number) => void;
}) {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => setRaw(String(value)), [value]);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        step={1}
        min={min}
        max={max}
        disabled={disabled}
        value={raw}
        onChange={(event) => setRaw(event.target.value)}
        onBlur={() => {
          const parsed = Number(raw);
          if (!Number.isInteger(parsed)) { onCommit(value); return; }
          onCommit(clamp(parsed, min, max));
        }}
      />
    </div>
  );
}

function PlacementEditor({ widget, minSize, issues, disabled, onChange, onRemove }: {
  widget: KioskLayoutWidget;
  minSize: { w: number; h: number };
  issues: PlacementIssue[];
  disabled: boolean;
  onChange: (next: KioskLayoutWidget) => void;
  onRemove: () => void;
}) {
  const issueFor = (field: PlacementIssue['field']) => issues.filter((issue) => issue.field === field);
  const commit = (patch: Partial<KioskLayoutWidget['position']>, zIndex = widget.zIndex) =>
    onChange({ ...widget, position: { ...widget.position, ...patch }, zIndex });

  // The legal size window depends on the origin: a widget can only grow up to
  // the canvas edge from where it currently sits. When the catalog minimum no
  // longer fits at this position the field is unusable, so say why instead of
  // clamping every entry into an impossible range.
  const maxW = CANVAS.width - widget.position.x;
  const maxH = CANVAS.height - widget.position.y;
  const widthFits = maxW >= minSize.w;
  const heightFits = maxH >= minSize.h;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Integer CSS pixels on the {CANVAS.width}×{CANVAS.height} canvas, origin top-left. This widget needs at least{' '}
        {minSize.w}×{minSize.h}px.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <NumberField id="placement-x" label="x" value={widget.position.x} min={0} max={CANVAS.width - widget.position.w} disabled={disabled} onCommit={(x) => commit({ x })} />
        <NumberField id="placement-y" label="y" value={widget.position.y} min={0} max={CANVAS.height - widget.position.h} disabled={disabled} onCommit={(y) => commit({ y })} />
        <NumberField id="placement-w" label="width" value={widget.position.w} min={1} max={Math.max(1, maxW)} disabled={disabled || !widthFits} onCommit={(w) => commit({ w })} />
        <NumberField id="placement-h" label="height" value={widget.position.h} min={1} max={Math.max(1, maxH)} disabled={disabled || !heightFits} onCommit={(h) => commit({ h })} />
      </div>
      {(!widthFits || !heightFits) && (
        <p role="alert" className="text-xs text-destructive">
          {!widthFits && <>Only {maxW}px of width remain at x={widget.position.x}, below this widget&apos;s {minSize.w}px minimum. </>}
          {!heightFits && <>Only {maxH}px of height remain at y={widget.position.y}, below this widget&apos;s {minSize.h}px minimum. </>}
          Move the widget left/up to resize it.
        </p>
      )}
      <NumberField
        id="placement-z"
        label={`Layer (zIndex ${GEOMETRY.zIndex.min}–${GEOMETRY.zIndex.max})`}
        value={widget.zIndex}
        min={GEOMETRY.zIndex.min}
        max={GEOMETRY.zIndex.max}
        disabled={disabled}
        onCommit={(zIndex) => commit({}, zIndex)}
      />
      {issues.length > 0 && (
        <ul role="alert" aria-live="polite" className="space-y-1 text-xs text-destructive">
          {issues.map((issue, index) => <li key={index}>{issue.message}</li>)}
        </ul>
      )}
      <Button type="button" size="sm" variant="destructive" className="min-h-[44px]" disabled={disabled} onClick={onRemove}>
        Remove widget
      </Button>
    </div>
  );
}

function ConfigFieldsEditor({ descriptor, config, disabled, onChange }: {
  descriptor: KioskWidgetDescriptor;
  config: Record<string, unknown>;
  disabled: boolean;
  onChange: (name: string, value: unknown) => void;
}) {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function commitNumber(name: string, label: string, raw: string, fallback: unknown) {
    if (raw.trim() === '') { onChange(name, undefined); return; }
    const parsed = Number(raw);
    if (!Number.isInteger(parsed)) {
      setFieldErrors((current) => ({ ...current, [name]: `${label} must be a whole number.` }));
      return;
    }
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
    onChange(name, parsed);
    void fallback;
  }

  return (
    <div className="space-y-3">
      {descriptor.fieldDescriptors.map((field) => {
        const value = config[field.name];
        const fieldId = `config-${descriptor.type}-${field.name}`;
        const enumOptions = (descriptor.configSchema.properties[field.name]?.enum as string[] | undefined)
          ?? field.options?.map((option) => option.value);
        return (
          <div key={field.name} className="space-y-1">
            <Label htmlFor={fieldId}>
              {field.label}
              {field.required ? <span className="text-destructive"> *</span> : null}
            </Label>
            {field.type === 'textarea' && (
              <Textarea
                id={fieldId}
                disabled={disabled}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )}
            {field.type === 'boolean' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={fieldId}
                  checked={value === true}
                  disabled={disabled}
                  onCheckedChange={(checked) => onChange(field.name, checked === true)}
                />
                <span className="text-xs text-muted-foreground">Enabled</span>
              </div>
            )}
            {field.type === 'number' && (
              <Input
                id={fieldId}
                type="number"
                step={1}
                inputMode="numeric"
                disabled={disabled}
                value={typeof value === 'number' ? String(value) : ''}
                onChange={(event) => commitNumber(field.name, field.label, event.target.value, value)}
              />
            )}
            {field.type === 'select' && (
              <select
                id={fieldId}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                disabled={disabled}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => onChange(field.name, event.target.value)}
              >
                <option value="">Not set</option>
                {(enumOptions ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            )}
            {(field.type === 'text' || field.type === 'asset' || field.type === 'id-list') && (
              <Input
                id={fieldId}
                disabled={disabled}
                placeholder={field.type === 'id-list' ? 'Comma-separated record IDs' : undefined}
                value={
                  field.type === 'id-list'
                    ? Array.isArray(value) ? value.join(', ') : ''
                    : typeof value === 'string' ? value : ''
                }
                onChange={(event) => onChange(
                  field.name,
                  field.type === 'id-list'
                    ? Array.from(new Set(parseIdList(event.target.value)))
                    : event.target.value,
                )}
              />
            )}
            {field.type === 'id-list' && (
              <p className="text-xs text-muted-foreground">
                At most {GEOMETRY.maxVisibilityIdsPerRule} IDs, each up to {GEOMETRY.maxIdLength} characters. The service validates the schema on save.
              </p>
            )}
            {fieldErrors[field.name] && <p role="alert" className="text-xs text-destructive">{fieldErrors[field.name]}</p>}
          </div>
        );
      })}
    </div>
  );
}

function VisibilityRuleEditor({ collection, rule, disabled, onChange }: {
  collection: 'categories' | 'products' | 'featuredProducts' | 'discounts';
  rule: { allowIds: string[]; denyIds: string[] };
  disabled: boolean;
  onChange: (key: 'allowIds' | 'denyIds', raw: string) => void;
}) {
  const title = collection === 'featuredProducts' ? 'Featured products' : collection.charAt(0).toUpperCase() + collection.slice(1);
  return (
    <fieldset className="space-y-2 rounded border p-2">
      <legend className="px-1 text-xs font-semibold">{title}</legend>
      {(['allowIds', 'denyIds'] as const).map((key) => (
        <div key={key} className="space-y-1">
          <Label htmlFor={`visibility-${collection}-${key}`} className="text-xs">
            {key === 'allowIds' ? 'Allow IDs (empty = all eligible)' : 'Deny IDs (always wins)'}
          </Label>
          <Textarea
            id={`visibility-${collection}-${key}`}
            className="min-h-14"
            disabled={disabled}
            value={rule[key].join(', ')}
            onChange={(event) => onChange(key, event.target.value)}
          />
          <p className="text-xs text-muted-foreground" aria-live="polite">{rule[key].length} selected</p>
        </div>
      ))}
    </fieldset>
  );
}

function PreviewWidgetBox({ placed, scale, discountState }: {
  placed: PlacedWidget;
  scale: number;
  discountState: DiscountState;
}) {
  const rect = scaleRect(placed.rect, scale);
  const slots = placed.contract.dataSlots;
  const showsDiscount = placed.contract.stateVariants.length > 0;
  return (
    <div
      className="absolute flex flex-col items-center justify-center gap-0.5 overflow-hidden rounded-sm border border-primary/30 bg-muted/40 p-1 text-center"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
      aria-hidden="true"
    >
      <span className="truncate text-[10px] font-medium">{placed.widget.type}</span>
      {slots.length > 0 && <span className="text-[9px] text-muted-foreground">data: {slots.join(', ')}</span>}
      {showsDiscount && <span className="text-[9px] text-muted-foreground">discount state: {discountState}</span>}
    </div>
  );
}

/**
 * Renders the frozen 7-element/7-state discount contract for the selected
 * state using boolean-only presentation flags from the shared renderer.
 * Values are the labeled fixture strings; nothing is ever calculated.
 */
function DiscountPreview({ state }: { state: DiscountState }) {
  const { presentation, displayedKeys } = selectDisplayableDiscount(DISCOUNT_FIXTURE_FIELDS, state);
  const labels: Record<(typeof DISCOUNT_FIELD_KEYS)[number], string> = {
    originalPrice: 'Original price',
    discountedPrice: 'Discounted price',
    percentOff: 'Percent off',
    amountSaved: 'Amount saved',
    expiry: 'Expiry',
    promoLabel: 'Promo label',
    conditions: 'Conditions',
  };
  const fieldLabelOverrides: Partial<Record<(typeof DISCOUNT_FIELD_KEYS)[number], string>> = {
    expiry: 'Expiry (returned label)',
  };
  const strikeApplies = (key: (typeof DISCOUNT_FIELD_KEYS)[number]) =>
    key === 'originalPrice' && presentation.strikeThroughOriginal;
  return (
    <dl className="grid grid-cols-2 gap-2 text-sm" aria-live="polite">
      {displayedKeys.map((key) => (
        <div key={key}>
          <dt className="text-xs text-muted-foreground">{fieldLabelOverrides[key] ?? labels[key]}</dt>
          <dd className={strikeApplies(key) ? 'line-through' : undefined}>
            {DISCOUNT_FIXTURE_FIELDS[key] ?? ''}
          </dd>
        </div>
      ))}
      {displayedKeys.length === 0 && (
        <p className="col-span-2 text-xs text-muted-foreground">
          This state shows no discount fields. Copy for “{state}”:{' '}
          {state === 'no-discount'
            ? 'ordinary server price only; no badge or “save” claim.'
            : state === 'ineligible'
              ? 'This offer isn’t applied to your items.'
              : state === 'expired'
                ? 'This offer is no longer available.'
                : state === 'pending-quote'
                  ? 'Updating price…'
                  : state === 'quote-failed'
                    ? 'We couldn’t update the price. Try again.'
                    : 'Price updated — review before paying.'}
        </p>
      )}
      {presentation.showDiscountBadge && displayedKeys.length > 0 && (
        <p className="col-span-2 text-xs font-medium text-muted-foreground">Discount badge shown (no “save” claim where the server did not return one).</p>
      )}
    </dl>
  );
}
